import { Button, Checkbox, Col, DatePicker, DatePickerProps, Form, Row, Select, Table, Tag } from "antd";
import Column from "antd/es/table/Column";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { ContentHeader } from "../../components";
import ModalTextForm from "../../components/modal/ModalTextForm";
import { Links } from "../../helpers/Links";
import { dateCommon, validateMessages } from '../../helpers/common';
import { BaoHiem } from "../../models/baohiem/BaoHiem";
import { DanhSachKhenThuongUngHoResponse } from "../../models/luong/Luong";
import { layDsBaoHiemTheoNam } from "../../services/baohiem";
import { khoiTaoLuongCoBan, layDsKhenThuongUngHoTheoNam, layDsLuongCoBan, layDsLuongCoBanTamUngTheoNam } from "../../services/luong";
import { hideLoadingScreen, showLoadingScreen } from "../../store/reducers/loading";

const optionsLoai = [
    { value: 0, label: "Chính/Điều chỉnh" },
    { value: 1, label: "Tạm tính" },
];

interface LuongCoBanItem {
    id: number;
    thangNam: string;
    thang: number;
    nam: number;
    congThang: number;
    loai: number;
    isTruCongDoan: boolean;
    idLuongTamUng?: number;
    tenKyLuong: string;
    ngayKhoiTao: string;
    ngayCapNhat?: string;
    tenNVKhoiTao?: string;
}

const DanhSachLuongCoBan = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const ngayHienTai = (dayjs()).date() <= 10 ? (dayjs()).subtract(1, 'month') : dayjs();

    const [loading, setLoading] = useState<boolean>(false);
    const [dataDs, setDataDs] = useState<Array<LuongCoBanItem>>([]);
    const [ngayThang, setNgayThang] = useState<Dayjs | null>(null);
    const [loaiFilter, setLoaiFilter] = useState<number | undefined>(undefined);

    const [openModalKhoiTao, setOpenModalKhoiTao] = useState<boolean>(false);
    const [kyLuongKhoiTao, setKyLuongKhoiTao] = useState<Dayjs | null>(ngayHienTai);
    const [dsBaoHiem, setDsBaoHiem] = useState<Array<BaoHiem>>([]);
    const [dsKhenThuongUngHo, setDsKhenThuongUngHo] = useState<Array<DanhSachKhenThuongUngHoResponse>>([]);
    const [namLocBaoHiem, setNamLocBaoHiem] = useState<Dayjs | null>(null);
    const [namLocKhenThuong, setNamLocKhenThuong] = useState<Dayjs | null>(null);
    const [namLocTamUng, setNamLocTamUng] = useState<Dayjs | null>(null);

    const loaiWatch = Form.useWatch('loai', form);
    const kyLuongWatch = Form.useWatch('kyLuong', form);
    const [dsTamUng, setDsTamUng] = useState<Array<LuongCoBanItem>>([]);

    const layDsTamUngModal = async (nam?: number) => {
        try {
            const tamung = await layDsLuongCoBanTamUngTheoNam(nam);
            setDsTamUng(tamung ?? []);
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        }
    };

    const layDuLieu = async (thangNam?: string, loai?: number) => {
        setLoading(true);
        try {
            const rs = await layDsLuongCoBan(0, 1000, thangNam, loai);
            setDataDs(rs?.data ?? []);
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        }
        setLoading(false);
    };

    useEffect(() => {
        const thangNam = ngayThang ? ngayThang.format('MM/YYYY') : undefined;
        layDuLieu(thangNam, loaiFilter);
    }, []);

    const monthFormat = 'MM/YYYY';
    const thangNamFormat: DatePickerProps['format'] = (value) => `Tháng ${value.format(monthFormat)}`;

    const onChangeThangNam: DatePickerProps['onChange'] = (date) => {
        setNgayThang(date);
        layDuLieu(date ? date.format('MM/YYYY') : undefined, loaiFilter);
    };

    const onChangeLoaiFilter = (val: number | undefined) => {
        setLoaiFilter(val);
        layDuLieu(ngayThang ? ngayThang.format('MM/YYYY') : undefined, val);
    };

    const layDsBaoHiemModal = async (nam?: number) => {
        try {
            const bh = await layDsBaoHiemTheoNam(nam);
            setDsBaoHiem(bh ?? []);
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        }
    };

    const layDsKhenThuongUngHoModal = async (nam?: number) => {
        try {
            const ktuh = await layDsKhenThuongUngHoTheoNam(nam);
            setDsKhenThuongUngHo(ktuh ?? []);
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        }
    };

    const onMoModalKhoiTao = async () => {
        const ky = ngayThang ?? ngayHienTai;
        setKyLuongKhoiTao(ky);
        const namKy = dayjs().year(ky.year());
        setNamLocBaoHiem(namKy);
        setNamLocKhenThuong(namKy);
        setNamLocTamUng(namKy);
        form.setFieldsValue({
            kyLuong: ky,
            loai: 1,
            isTruCongDoan: false,
            dsIdBaoHiemThang: [],
            dsIdLuongKhenThuongUngHo: [],
            idLuongTamUng: undefined,
        });
        await Promise.all([
            layDsBaoHiemModal(ky.year()),
            layDsKhenThuongUngHoModal(ky.year()),
            layDsTamUngModal(ky.year()),
        ]);
        setOpenModalKhoiTao(true);
    };

    const onChangeKyLuongKhoiTao = async (date: Dayjs | null) => {
        setKyLuongKhoiTao(date);
    };

    const onChangeNamLocBaoHiem = async (date: Dayjs | null) => {
        setNamLocBaoHiem(date);
        form.setFieldsValue({ dsIdBaoHiemThang: [] });
        await layDsBaoHiemModal(date ? date.year() : undefined);
    };

    const onChangeNamLocKhenThuong = async (date: Dayjs | null) => {
        setNamLocKhenThuong(date);
        form.setFieldsValue({ dsIdLuongKhenThuongUngHo: [] });
        await layDsKhenThuongUngHoModal(date ? date.year() : undefined);
    };

    const onChangeNamLocTamUng = async (date: Dayjs | null) => {
        setNamLocTamUng(date);
        form.setFieldsValue({ idLuongTamUng: undefined });
        await layDsTamUngModal(date ? date.year() : undefined);
    };

    const onFinishKhoiTao = async (values: any) => {
        dispatch(showLoadingScreen());
        try {
            const ky = values?.kyLuong as Dayjs;
            const request = {
                thangNam: ky.format('MM/YYYY'),
                loai: parseInt(values?.loai),
                idLuongTamUng: values?.idLuongTamUng ? parseInt(values.idLuongTamUng) : null,
                isTruCongDoan: !!values?.isTruCongDoan,
                dsIdBaoHiemThang: (values?.dsIdBaoHiemThang ?? []) as number[],
                dsIdLuongKhenThuongUngHo: (values?.dsIdLuongKhenThuongUngHo ?? []) as number[],
            };
            const rs = await khoiTaoLuongCoBan(request);
            if (rs && rs.id > 0) {
                toast.success('Khởi tạo lương cơ bản thành công!');
                setOpenModalKhoiTao(false);
                layDuLieu(ngayThang ? ngayThang.format('MM/YYYY') : undefined, loaiFilter);
            } else {
                toast.error('Khởi tạo lương cơ bản thất bại!');
            }
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        }
        dispatch(hideLoadingScreen());
    };

    const optionsBaoHiem = (dsBaoHiem || []).map((x: any) => ({
        value: x.id,
        label: `Tháng ${dayjs(x.thangNam).format('MM/YYYY')}${x.loaiBaoHiemThang === 1 ? ' - Bổ sung/Điều chỉnh' : ''}`,
    }));

    const optionsKhenThuongUngHo = (dsKhenThuongUngHo || []).map((x) => ({
        value: x.id,
        label: `${x.ten} (${x.thoiGian})`,
    }));

    return (
        <section>
            {openModalKhoiTao && (
                <ModalTextForm
                    title="Khởi tạo lương cơ bản"
                    hideFooter
                    show={openModalKhoiTao}
                    handleClose={() => setOpenModalKhoiTao(false)}
                >
                    <Form
                        layout="vertical"
                        validateMessages={validateMessages}
                        onFinish={onFinishKhoiTao}
                        form={form}
                    >
                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item
                                    rules={[{ required: true }]}
                                    label="Kỳ lương"
                                    name="kyLuong"
                                >
                                    <DatePicker
                                        changeOnBlur={true}
                                        picker="month"
                                        format={["MM/YYYY", "MMYYYY"]}
                                        placeholder="Chọn kỳ lương"
                                        style={{ width: "100%" }}
                                        onChange={onChangeKyLuongKhoiTao}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    rules={[{ required: true }]}
                                    label="Loại"
                                    name="loai"
                                >
                                    <Select
                                        notFoundContent="Không có dữ liệu"
                                        options={optionsLoai}
                                        placeholder="Loại"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        {loaiWatch === 0 && (
                            <Row gutter={8}>
                                <Col span={8}>
                                    <Form.Item label="Năm tạm ứng">
                                        <DatePicker
                                            changeOnBlur={true}
                                            picker="year"
                                            placeholder="Năm (để trống = tất cả)"
                                            style={{ width: "100%" }}
                                            value={namLocTamUng}
                                            onChange={onChangeNamLocTamUng}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={16}>
                                    <Form.Item
                                        rules={[{ required: true, message: 'Vui lòng chọn bảng lương tạm ứng!' }]}
                                        label="Bảng lương tạm ứng khấu trừ"
                                        name="idLuongTamUng"
                                    >
                                        <Select
                                            placeholder="Chọn bảng lương tạm ứng"
                                            options={(dsTamUng || []).map((x) => ({
                                                value: x.id,
                                                label: `${x.tenKyLuong} (Ngày tạo: ${dateCommon.formatDate(x.ngayKhoiTao)})`,
                                            }))}
                                            allowClear
                                            notFoundContent="Không có dữ liệu bảng lương tạm ứng"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                        <Row gutter={8}>
                            <Col span={8}>
                                <Form.Item label="Năm bảo hiểm">
                                    <DatePicker
                                        changeOnBlur={true}
                                        picker="year"
                                        placeholder="Năm (để trống = tất cả)"
                                        style={{ width: "100%" }}
                                        value={namLocBaoHiem}
                                        onChange={onChangeNamLocBaoHiem}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={16}>
                                <Form.Item label="Bảo hiểm áp dụng" name="dsIdBaoHiemThang">
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        placeholder="Chọn các kỳ bảo hiểm"
                                        options={optionsBaoHiem}
                                        notFoundContent="Không có dữ liệu"
                                        filterOption={(input, option: any) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={8}>
                                <Form.Item label="Năm tăng/giảm">
                                    <DatePicker
                                        changeOnBlur={true}
                                        picker="year"
                                        placeholder="Năm (để trống = tất cả)"
                                        style={{ width: "100%" }}
                                        value={namLocKhenThuong}
                                        onChange={onChangeNamLocKhenThuong}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={16}>
                                <Form.Item label="Tăng/giảm áp dụng" name="dsIdLuongKhenThuongUngHo">
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        placeholder="Chọn các kỳ tăng/giảm"
                                        options={optionsKhenThuongUngHo}
                                        notFoundContent="Không có dữ liệu"
                                        filterOption={(input, option: any) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="isTruCongDoan" valuePropName="checked">
                            <Checkbox>Trừ kinh phí công đoàn</Checkbox>
                        </Form.Item>
                        <Form.Item>
                            <Button
                                type="primary"
                                className="float-right"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                            >
                                Khởi tạo
                            </Button>
                        </Form.Item>
                    </Form>
                </ModalTextForm>
            )}

            <ContentHeader title="Danh sách lương cơ bản" />

            <Row className="form-group" gutter={8}>
                <Col span={6}>
                    <DatePicker
                        changeOnBlur={true}
                        value={ngayThang}
                        style={{ width: "100%" }}
                        placeholder="Tháng tính lương"
                        picker="month"
                        format={thangNamFormat}
                        onChange={onChangeThangNam}
                    />
                </Col>
                <Col span={6}>
                    <Select
                        allowClear
                        style={{ width: "100%" }}
                        placeholder="Loại lương"
                        options={optionsLoai}
                        value={loaiFilter}
                        onChange={onChangeLoaiFilter}
                    />
                </Col>
                <Col span={12}>
                    <Button
                        className="hrm-btn hrm-btn-warning float-right"
                        onClick={onMoModalKhoiTao}
                        icon={<PlusOutlined />}
                    >
                        Khởi tạo lương cơ bản
                    </Button>
                </Col>
            </Row>

            <Row gutter={8}>
                <Col span={24}>
                    <div className="card card-primary">
                        <Row>
                            <Col span={24}>
                                <Table
                                    locale={{ emptyText: "Không có dữ liệu" }}
                                    dataSource={(dataDs || []).map((value, index) => ({ key: index, ...value }))}
                                    loading={loading}
                                >
                                    <Column
                                        title="Kỳ lương"
                                        key="tenKyLuong"
                                        align="left"
                                        render={(value) => (
                                            <div className="text-align-middle">{value?.tenKyLuong}</div>
                                        )}
                                    />
                                    <Column
                                        title="Loại"
                                        key="loai"
                                        align="left"
                                        render={(value) => (
                                            <div className="text-align-middle">{value?.loai == 0 ? "Chính/Điều chỉnh" : "Tạm tính"}</div>
                                        )}
                                    />
                                    <Column
                                        title="Công tháng"
                                        key="congThang"
                                        align="left"
                                        render={(value) => (
                                            <div className="text-align-middle">{value?.congThang}</div>
                                        )}
                                    />
                                    <Column
                                        title="Trừ công đoàn"
                                        key="isTruCongDoan"
                                        align="left"
                                        render={(value) => (
                                            <div className="text-align-middle">{value?.isTruCongDoan ? "Có" : "Không"}</div>
                                        )}
                                    />
                                    <Column
                                        title="Trạng thái"
                                        key="trangThai"
                                        align="left"
                                        render={(value) => (
                                            value?.trangThai === 0 ? (<Tag color="blue">Khởi tạo</Tag>) : (<Tag color="success">Đã xác nhận</Tag>)
                                        )}
                                    />
                                    <Column
                                        title="Người khởi tạo"
                                        key="tenNVKhoiTao"
                                        align="left"
                                        render={(value) => (
                                            <div className="text-align-middle">{value?.tenNVKhoiTao}</div>
                                        )}
                                    />
                                    <Column
                                        title="Ngày khởi tạo"
                                        key="ngayKhoiTao"
                                        align="left"
                                        render={(value) => (
                                            <div className="text-align-middle">{dateCommon.formatDate(value?.ngayKhoiTao)}</div>
                                        )}
                                    />
                                    <Column
                                        title=""
                                        key="action"
                                        render={(value, _record, index) => (
                                            <Link
                                                key={"link" + index}
                                                className="hrm-btn hrm-btn-sm btn-primary"
                                                to={`${Links.Luong.DanhSachLuongCoBan}/chitiet?id=${value?.id}`}
                                            >
                                                Xem
                                            </Link>
                                        )}
                                    />
                                </Table>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export default DanhSachLuongCoBan;
